import { onUnmounted, ref } from "vue";
import { itineraryRepository } from "../repositories/itinerary.repository";
import { isTerminalPdfJobStatus } from "../models/itinerary-pdf-job.schema";
import { useItineraryI18n } from "./useItineraryI18n";
// =============================================================================
// CONSTANTS
// =============================================================================
const POLL_INTERVAL_MS = 3000;
const POLL_MAX_ATTEMPTS = 20;
// =============================================================================
// COMPOSABLE
// =============================================================================
export function useItineraryPdfDownload() {
    const { t } = useItineraryI18n();
    const isDownloading = ref(false);
    const statusMessage = ref(null);
    const errorMessage = ref("");
    let abortController = null;
    let pollTimer = null;
    function clearStream() {
        abortController?.abort();
        abortController = null;
    }
    function clearPoll() {
        if (pollTimer !== null) {
            clearTimeout(pollTimer);
            pollTimer = null;
        }
    }
    function extractErrorMessage(error) {
        if (error instanceof Error && error.message)
            return error.message;
        return t("itinerary.pdf.error", "Error al generar el PDF");
    }
    function statusLabelFor(status) {
        return status === "waiting_translation"
            ? t("itinerary.pdf.waiting_translation", "Traduciendo contenido...")
            : t("itinerary.pdf.processing", "Generando PDF...");
    }
    async function pollForCompletion(itineraryId) {
        let attempts = 0;
        return new Promise((resolve) => {
            const tick = async () => {
                attempts += 1;
                try {
                    const result = await itineraryRepository.downloadPdf(itineraryId);
                    if (result.status === "ready") {
                        window.location.assign(result.url);
                        resolve();
                        return;
                    }
                }
                catch (error) {
                    console.error("[useItineraryPdfDownload] Polling error:", error);
                }
                if (attempts >= POLL_MAX_ATTEMPTS) {
                    errorMessage.value = t("itinerary.pdf.timeout", "Tiempo de espera agotado. Intenta nuevamente.");
                    resolve();
                    return;
                }
                pollTimer = setTimeout(tick, POLL_INTERVAL_MS);
            };
            pollTimer = setTimeout(tick, POLL_INTERVAL_MS);
        });
    }
    async function openStream(itineraryId, jobId) {
        let reachedTerminal = false;
        const controller = new AbortController();
        abortController = controller;
        const callbacks = {
            onStatusChange(event) {
                if (event.status === "processing" || event.status === "waiting_translation") {
                    statusMessage.value = statusLabelFor(event.status);
                    return;
                }
                if (!isTerminalPdfJobStatus(event.status))
                    return;
                reachedTerminal = true;
                statusMessage.value = null;
                if (event.status === "completed") {
                    if (event.url)
                        window.location.assign(event.url);
                    return;
                }
                errorMessage.value =
                    event.failureSummary ??
                        t(event.status === "skipped"
                            ? "itinerary.pdf.skipped"
                            : "itinerary.pdf.failed", event.status === "skipped"
                            ? "La generación del PDF fue omitida"
                            : "No se pudo generar el PDF");
            },
            onError(error) {
                console.error("[useItineraryPdfDownload] SSE parse error:", error);
            },
        };
        try {
            await itineraryRepository.streamPdfJobEvents(itineraryId, jobId, callbacks, {
                signal: controller.signal,
            });
        }
        catch (error) {
            console.error("[useItineraryPdfDownload] Stream failed, falling back to polling:", error);
        }
        finally {
            abortController = null;
        }
        if (!reachedTerminal) {
            statusMessage.value = null;
            await pollForCompletion(itineraryId);
        }
    }
    async function downloadPdf(itineraryId) {
        if (isDownloading.value)
            return;
        clearStream();
        clearPoll();
        errorMessage.value = "";
        statusMessage.value = null;
        isDownloading.value = true;
        try {
            const result = await itineraryRepository.downloadPdf(itineraryId);
            if (result.status === "ready") {
                window.location.assign(result.url);
                return;
            }
            await openStream(itineraryId, result.job_id);
        }
        catch (error) {
            console.error("[useItineraryPdfDownload] Error downloading PDF:", error);
            errorMessage.value = extractErrorMessage(error);
        }
        finally {
            isDownloading.value = false;
        }
    }
    onUnmounted(() => {
        clearStream();
        clearPoll();
    });
    return {
        isDownloading,
        statusMessage,
        errorMessage,
        downloadPdf,
    };
}
export default useItineraryPdfDownload;
